import { FC, useMemo } from 'react';
import {
  DataObjectForm,
  DataObjectFormDataType,
  DataObjectType,
  DataObjectView,
  defaultExecution,
  DataObjectOutputType,
  DataObjectFormType,
  useSystemClassifierItem,
  useUserInfo,
  ISODate,
  useSystemClassifiers,
  DataObjectContextProvider,
  DimensionValidFormDataType,
  PermissionCode,
  useHasPermission,
} from '@atlas-frontend/sdk';
import { RouteObject } from '../../common/types';
import { Forbidden } from '../Forbidden';
import {
  DataObjectBreadcrumbs,
  DataObjectBreadcrumbsData,
} from './DataObjectBreadcrumbs';
import { useOpenTestCalculationReport } from './useOpenTestCalculation';
import { useOpenTestValidationReport } from './useOpenTestValidation';

export const DataObjectCreate: FC<{
  routes: Array<RouteObject<DataObjectBreadcrumbsData>>;
  view: DataObjectView;
  onSuccess: (params?: { id: number; mode?: string }) => void;
  onCancel: (params?: { id: number; mode?: string }) => void;
}> = (props) => (
  <Forbidden
    isAllowed={useHasPermission(
      props.view.dataObjectType === DataObjectType.IN
        ? PermissionCode.INPUT_DESC_MANAGE
        : PermissionCode.OUTPUT_DESC_MANAGE,
    )}
  >
    <DataObjectContextProvider>
      <DataObjectCreateInner {...props} />
    </DataObjectContextProvider>
  </Forbidden>
);

const DataObjectCreateInner: FC<{
  routes: Array<RouteObject<DataObjectBreadcrumbsData>>;
  view: DataObjectView;
  onSuccess: (params?: { id: number; mode?: string }) => void;
  onCancel: (params?: { id: number; mode?: string }) => void;
}> = (props) => {
  const { routes, view, onSuccess, onCancel } = props;
  const { dataObjectType } = view;
  const { manual: { id: manualId } = {} } = useDefaultVersionAttributes();
  const openTestCalculationReport = useOpenTestCalculationReport();
  const openTestValidationReport = useOpenTestValidationReport();

  const { user } = useUserInfo();
  const {
    DIMENSION_TYPE: { systemClassifierItems: dimensionTypes },
    DEFAULT_INDICATOR_DIMENSION_WIDTH: {
      systemClassifierItems: [defaultWidth],
    },
  } = useSystemClassifiers(
    'DIMENSION_TYPE',
    'DEFAULT_INDICATOR_DIMENSION_WIDTH',
  );

  const dataObject = useMemo(() => {
    const outputTypeCode =
      dataObjectType === DataObjectType.OUT
        ? DataObjectOutputType.OUTPUT
        : undefined;
    const formType =
      dataObjectType === DataObjectType.OUT
        ? DataObjectFormType.OUTPUT
        : DataObjectFormType.IN;
    return {
      groupAndSubgroupId: '',
      statusCode: 'DRAFT',
      versionNumber: '1',
      versionId: 0,
      dataObjectType,
      outputTypeCode,
      formType,
      confidential: false,
      subjectDimension: true,
      indicatorStatus: false,
      allVersions: [],
      execution: defaultExecution({ executionMethodId: manualId }),
      executionDeadlines: [],
      modifiedAt: null,
      modifiedBy: null,
      algorithmsCount: 0,
      validPeriod: ['' as ISODate, null],
      responsibleId: user.id,
      responsibleUsername: user.username,
      dimensions: [
        {
          code: 'SUBJEKT',
          name: 'Andmesubjekt',
          description: null,
          width: Number(defaultWidth.code),
          sequenceNumber: 1,
          classifier: null,
          constantValue: null,
          aggregateHierarchy: null,
          required: true,
          dimensionType:
            dimensionTypes.find((item) => item.code === 'SUBJECT') ?? null,
        } as DimensionValidFormDataType,
      ],
      validationRules: [],
      requiredKeys: [],
      alternativeAlgorithmSets: [],
      aggregateValues: true,
      hasActiveSubmittedDataVersions: false,
      hasRealData: false,
    } as DataObjectFormDataType;
  }, [dataObjectType, manualId, dimensionTypes]);

  return (
    <DataObjectForm
      dataObjectView={view}
      mode="create"
      dataObjectDescriptionVersion={dataObject}
      onSuccess={onSuccess}
      onClickCancel={onCancel}
      breadCrumb={<DataObjectBreadcrumbs routes={routes} />}
      openTestCalculate={openTestCalculationReport}
      openTestValidate={openTestValidationReport}
    />
  );
};

function useDefaultVersionAttributes() {
  const manual = useSystemClassifierItem('EXECUTION_METHOD', 'MANUAL');

  return { manual };
}
